// Form-based call-flow editor (phase 3 of the call-flows plan). Admin-only —
// the render site gates on isAdmin and the backend enforces requireAdmin.
// Deliberately NOT a drag-and-drop canvas: structured forms + live preview.
//
// Besides the routing the diagram shows, the editor captures what the
// interactive phone-menu guide needs: structured weekly hours and holidays,
// prompts (audio / text-to-speech), per-language display names, queue agents,
// queue behaviour and exception handling. All of that is optional.

import { useDeferredValue, useMemo, useState, type ReactNode } from 'react';
import { X, Plus, Trash2, AlertCircle, ChevronRight } from 'lucide-react';
import {
  CallFlow, CallFlowInput, CallFlowNode, AutoAttendantNode, QueueFlowNode, Group, Prompt,
} from '../api/client';
import { FlowDiagram, useFlowLayout } from './CallFlowsViewer';
import { inputCls, labelCls, clampInt } from './callFlowEditor/common';
import { formatWeekly, parseLegacyHours, legacyHolidaysToRanges } from './callFlowEditor/weekly';
import { HoursGrid } from './callFlowEditor/HoursGrid';
import { HolidaysEditor } from './callFlowEditor/HolidaysEditor';
import { PromptEditor } from './callFlowEditor/PromptEditor';
import { LocalizedNameInput } from './callFlowEditor/LocalizedNameInput';
import { AgentsPicker, type AgentOption } from './callFlowEditor/AgentsPicker';
import { QueueExceptionForm } from './callFlowEditor/QueueExceptionForm';
import { QueueSettingsForm } from './callFlowEditor/QueueSettingsForm';

const LOCATION_ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

const NODE_TYPE_LABEL: Record<CallFlowNode['type'], string> = {
  autoAttendant: 'Auto attendant',
  queue: 'Call queue',
  voicemail: 'Voicemail',
  person: 'Person',
  external: 'External number',
  disconnect: 'Disconnect',
};

const ROUTING_OPTIONS: Array<{ value: NonNullable<QueueFlowNode['routing']>; label: string }> = [
  { value: 'roundRobin', label: 'Round robin' },
  { value: 'longestIdle', label: 'Longest idle' },
  { value: 'attendant', label: 'Attendant' },
  { value: 'serial', label: 'Serial' },
];

interface QueueOption { id: string; displayName: string; }

/** Injected by the host once the audio store exists (Phase 3): renders the
 *  chooser/uploader for an audio prompt. */
export type AudioPickerRenderer = (value: Prompt, onChange: (v: Prompt) => void) => ReactNode;

interface CallFlowEditorProps {
  /** null → create a new location */
  initial: CallFlow | null;
  groups: Group[];
  queues: QueueOption[];
  /** Directory users for the queue agents picker. */
  directory?: AgentOption[];
  accentColor: string;
  /** Import proposals pass false for not-yet-saved locations. Defaults to !!initial. */
  allowDelete?: boolean;
  audioPicker?: AudioPickerRenderer;
  onSave: (locationId: string, body: CallFlowInput) => Promise<void>;
  onDelete: (locationId: string) => Promise<void>;
  onCancel: () => void;
}

type Draft = CallFlowInput & { id: string };

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
}

function newFlowTemplate(): Draft {
  return {
    id: '',
    locationName: '',
    mainNumbers: [''],
    timezone: '',
    schedule: {
      hours: [{ days: 'Mo-Fr', open: '08:00', close: '17:00' }],
      holidays: [],
      weekly: { mon: [[480, 1020]], tue: [[480, 1020]], wed: [[480, 1020]], thu: [[480, 1020]], fri: [[480, 1020]] },
    },
    openTargetId: 'menu',
    closedTargetId: 'voicemail',
    nodes: [
      { id: 'menu', label: 'Welcome menu', type: 'autoAttendant', dialByName: false, menu: [{ key: '1', label: 'Main queue', targetId: 'queue-1' }] },
      { id: 'queue-1', label: 'Main queue', type: 'queue', routing: 'roundRobin' },
      { id: 'voicemail', label: 'Voicemail', type: 'voicemail', detail: '' },
    ],
    viewerGroupIds: [],
  };
}

/** Seed the structured hours/holidays from the legacy free-text rows when a
 *  doc predates them, so the admin edits a grid rather than strings. Rows the
 *  parser doesn't understand stay as they are (shown read-only). */
function seedDraft(initial: CallFlow): Draft {
  const draft: Draft = JSON.parse(JSON.stringify(initial));
  delete (draft as any).audio;
  delete (draft as any).sync;
  delete (draft as any).updatedAt;
  delete (draft as any).updatedBy;
  if (!draft.schedule.weekly) {
    const parsed = parseLegacyHours(draft.schedule.hours);
    if (parsed) draft.schedule.weekly = parsed;
  }
  if (!draft.schedule.holidayRanges && draft.schedule.holidays.length) {
    const ranges = legacyHolidaysToRanges(draft.schedule.holidays);
    if (ranges.length === draft.schedule.holidays.length) draft.schedule.holidayRanges = ranges;
  }
  return draft;
}

/** Every place a node id is referenced, for safe-delete checks. */
function referencesTo(draft: Draft, nodeId: string): string[] {
  const refs: string[] = [];
  if (draft.openTargetId === nodeId) refs.push('open-hours entry');
  if (draft.closedTargetId === nodeId) refs.push('closed-hours entry');
  for (const n of draft.nodes) {
    if (n.type === 'autoAttendant') {
      if (n.menu.some(m => m.targetId === nodeId)) refs.push(`menu of "${n.label}"`);
      if (n.operatorTargetId === nodeId) refs.push(`operator of "${n.label}"`);
      if (n.afterHoursTargetId === nodeId) refs.push(`after-hours of "${n.label}"`);
    } else if (n.type === 'queue') {
      if (n.overflowTargetId === nodeId || n.overflow?.targetId === nodeId) refs.push(`overflow of "${n.label}"`);
      if (n.timeoutTargetId === nodeId || n.timeout?.targetId === nodeId) refs.push(`timeout of "${n.label}"`);
      if (n.noAgent?.targetId === nodeId) refs.push(`no-agent handling of "${n.label}"`);
    }
  }
  return refs;
}

export function CallFlowEditor({ initial, groups, queues, directory = [], accentColor, allowDelete, audioPicker, onSave, onDelete, onCancel }: CallFlowEditorProps) {
  const [draft, setDraft] = useState<Draft>(() => (initial ? seedDraft(initial) : newFlowTemplate()));
  const [groupSearch, setGroupSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isNew = !initial;
  const locationId = isNew ? slugify(draft.locationName) : initial!.id;
  const canDelete = allowDelete ?? !isNew;
  const legacyHoursOnly = !draft.schedule.weekly && draft.schedule.hours.length > 0;
  const legacyHolidaysOnly = !draft.schedule.holidayRanges && draft.schedule.holidays.length > 0;

  const update = (patch: Partial<Draft>) => setDraft(d => ({ ...d, ...patch }));
  const updateSchedule = (patch: Partial<Draft['schedule']>) => setDraft(d => ({ ...d, schedule: { ...d.schedule, ...patch } }));
  const updateNode = (id: string, patch: any) =>
    setDraft(d => ({ ...d, nodes: d.nodes.map(n => (n.id === id ? { ...n, ...patch } : n)) }));

  function addNode(type: CallFlowNode['type']) {
    setDraft(d => {
      let i = 1;
      while (d.nodes.some(n => n.id === `${type.toLowerCase()}-${i}`)) i++;
      const id = `${type.toLowerCase()}-${i}`;
      const node: CallFlowNode = type === 'autoAttendant'
        ? { id, type, label: 'New menu', menu: [], dialByName: false }
        : type === 'queue'
          ? { id, type, label: 'New queue', routing: 'roundRobin' }
          : { id, type, label: NODE_TYPE_LABEL[type], detail: '' };
      return { ...d, nodes: [...d.nodes, node] };
    });
  }

  function removeNode(id: string) {
    const refs = referencesTo(draft, id);
    if (refs.length > 0) {
      setError(`Can't remove this node — it is still used by: ${refs.join(', ')}. Repoint those first.`);
      return;
    }
    if (draft.nodes.length <= 1) {
      setError("A flow needs at least one node.");
      return;
    }
    setError(null);
    setDraft(d => ({ ...d, nodes: d.nodes.filter(n => n.id !== id) }));
  }

  const nodeOptions = draft.nodes.map(n => ({ value: n.id, label: n.label }));
  const voicemailGroups = useMemo(() => {
    const seen = new Map<string, { id: string; name?: string }>();
    for (const g of draft.groups || []) seen.set(g.id, g);
    for (const n of draft.nodes) {
      if (n.type !== 'queue') continue;
      for (const ex of [n.overflow, n.timeout, n.noAgent]) {
        if (ex?.sharedVoicemailGroupId && !seen.has(ex.sharedVoicemailGroupId)) seen.set(ex.sharedVoicemailGroupId, { id: ex.sharedVoicemailGroupId });
      }
    }
    return [...seen.values()];
  }, [draft.groups, draft.nodes]);
  // Lay out the preview from a DEFERRED draft: keystrokes commit at high priority
  // so the form stays responsive, and the (synchronous) dagre pass runs at low
  // priority on the settled draft — collapsing intermediate keystrokes instead of
  // re-laying-out on every character (a full pass is ~tens of ms on large flows).
  const deferredDraft = useDeferredValue(draft);
  const previewFlow = useMemo<CallFlow>(() => ({ ...deferredDraft, id: locationId || 'new' }), [deferredDraft, locationId]);
  const previewNumbers = useMemo<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    for (const n of deferredDraft.nodes) { const p = (n as { phoneNumber?: string }).phoneNumber; if (p) m[n.id] = p; }
    return m;
  }, [deferredDraft]);
  const { layout: previewLayout, failed: previewFailed } = useFlowLayout(previewFlow, previewNumbers);
  const visibleGroups = useMemo(() => {
    const list = groups.filter(g => !g.orphaned);
    if (!groupSearch) return list.slice(0, 50);
    const s = groupSearch.toLowerCase();
    return list.filter(g => g.displayName.toLowerCase().includes(s)).slice(0, 50);
  }, [groups, groupSearch]);

  async function save() {
    setError(null);
    if (!draft.locationName.trim()) { setError('Location name is required.'); return; }
    if (!LOCATION_ID_RE.test(locationId)) { setError('Location name must contain at least one letter or digit.'); return; }
    const mainNumbers = draft.mainNumbers.map(n => n.trim()).filter(Boolean);
    if (mainNumbers.length === 0) { setError('At least one main number is required.'); return; }

    // The diagram reads the display rows; they are regenerated from the grid so
    // the two can never disagree. Legacy free-text rows survive only while no
    // structured hours exist.
    const weekly = draft.schedule.weekly;
    const holidayRanges = draft.schedule.holidayRanges?.filter(h => h.name.trim() && h.start && h.end);
    const body: CallFlowInput = {
      locationName: draft.locationName.trim(),
      mainNumbers,
      timezone: draft.timezone?.trim() || undefined,
      schedule: {
        hours: weekly ? formatWeekly(weekly) : draft.schedule.hours.filter(h => h.days.trim() && h.open.trim() && h.close.trim()),
        holidays: holidayRanges ? holidayRanges.map(h => ({ name: h.name.trim(), date: h.start.slice(0, 10) })) : draft.schedule.holidays.filter(h => h.name.trim()),
        weekly,
        holidayRanges,
      },
      openTargetId: draft.openTargetId,
      closedTargetId: draft.closedTargetId,
      nodes: draft.nodes,
      viewerGroupIds: draft.viewerGroupIds,
      displayName: draft.displayName,
      groups: draft.groups,
      source: draft.source,
    };
    setBusy(true);
    try {
      await onSave(locationId, body);
    } catch (e: any) {
      setError(e?.message || 'Save failed');
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    try {
      await onDelete(initial!.id);
    } catch (e: any) {
      setError(e?.message || 'Delete failed');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 overflow-y-auto" onClick={onCancel}>
      <div className="max-w-3xl mx-auto my-8 bg-white rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-lg z-10">
          <h2 className="text-base font-semibold text-gray-900">
            {isNew ? 'New location' : `Edit ${initial!.locationName}`}
          </h2>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Location */}
          <section>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Location name</label>
                <input type="text" value={draft.locationName} onChange={e => update({ locationName: e.target.value })}
                  placeholder="Bocholt" className={inputCls} />
                {isNew && locationId && <p className="text-[11px] text-gray-400 mt-1">id: {locationId}</p>}
                {draft.source && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    Imported from Teams as "{draft.source.autoAttendantName}" — if you rename it here, your name is kept on future imports.
                  </p>
                )}
              </div>
              <div>
                <label className={labelCls}>Timezone</label>
                <input type="text" value={draft.timezone || ''} onChange={e => update({ timezone: e.target.value })}
                  placeholder="W. Europe Standard Time" className={inputCls} />
              </div>
            </div>
            <div className="mt-3">
              <LocalizedNameInput value={draft.displayName} onChange={v => update({ displayName: v })} label="Title in the phone-menu guide" />
            </div>
            <div className="mt-3">
              <label className={labelCls}>Main numbers</label>
              {draft.mainNumbers.map((num, i) => (
                <div key={i} className="flex gap-2 mb-1.5">
                  <input type="text" value={num}
                    onChange={e => update({ mainNumbers: draft.mainNumbers.map((m, j) => (j === i ? e.target.value : m)) })}
                    placeholder="+49 2871 24680" className={inputCls} />
                  {draft.mainNumbers.length > 1 && (
                    <button onClick={() => update({ mainNumbers: draft.mainNumbers.filter((_, j) => j !== i) })}
                      className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
              {draft.mainNumbers.length < 5 && (
                <button onClick={() => update({ mainNumbers: [...draft.mainNumbers, ''] })}
                  className="text-xs text-purple-700 hover:underline flex items-center gap-1"><Plus className="w-3 h-3" />Add number</button>
              )}
            </div>
          </section>

          {/* Schedule */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Business hours</h3>
            <p className="text-xs text-gray-500 mb-2">When the main number routes to the open-hours menu. The guide shows "open now / closed now" from this, in the location's time zone.</p>
            {legacyHoursOnly && (
              <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                These hours were entered as text ({draft.schedule.hours.map(h => `${h.days} ${h.open}–${h.close}`).join(', ')}) and could not be converted automatically. Set them in the grid below to enable the open/closed status; the text rows are replaced on save.
              </div>
            )}
            <HoursGrid value={draft.schedule.weekly} onChange={weekly => updateSchedule({ weekly })} />

            <h3 className="text-sm font-semibold text-gray-900 mb-2 mt-4">Holidays</h3>
            {legacyHolidaysOnly && (
              <div className="mb-2 text-xs text-gray-500">
                Existing entries without a full date: {draft.schedule.holidays.map(h => `${h.name}${h.date ? ` (${h.date})` : ''}`).join(', ')}. Add them below with dates to have the status honour them; they are replaced on save once you do.
              </div>
            )}
            <HolidaysEditor value={draft.schedule.holidayRanges || []} onChange={holidayRanges => updateSchedule({ holidayRanges })} />
          </section>

          {/* Entry routing */}
          <section className="grid md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>During business hours, route to</label>
              <select value={draft.openTargetId} onChange={e => update({ openTargetId: e.target.value })} className={inputCls}>
                {nodeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Outside business hours, route to</label>
              <select value={draft.closedTargetId} onChange={e => update({ closedTargetId: e.target.value })} className={inputCls}>
                {nodeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </section>

          {/* Nodes */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">Flow nodes</h3>
              <div className="flex gap-1.5 flex-wrap justify-end">
                {(['autoAttendant', 'queue', 'voicemail', 'person', 'external', 'disconnect'] as const).map(t => (
                  <button key={t} onClick={() => addNode(t)}
                    className="px-2 py-1 text-[11px] border border-gray-300 rounded hover:bg-gray-50">+ {NODE_TYPE_LABEL[t]}</button>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              {draft.nodes.map(n => (
                <NodeForm key={n.id} node={n} nodeOptions={nodeOptions} groups={groups} queues={queues} directory={directory}
                  voicemailGroups={voicemailGroups} audioPicker={audioPicker}
                  onChange={patch => updateNode(n.id, patch)} onRemove={() => removeNode(n.id)} />
              ))}
            </div>
          </section>

          {/* Access */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Who can view this location</h3>
            <p className="text-xs text-gray-500 mb-2">Members of the selected security groups see this call flow in their Call flows tab. Admins always see every location.</p>
            <input type="text" value={groupSearch} onChange={e => setGroupSearch(e.target.value)}
              placeholder="Search security groups..." className={`${inputCls} mb-2`} />
            <div className="border border-gray-200 rounded max-h-44 overflow-y-auto">
              {visibleGroups.map(g => {
                const checked = draft.viewerGroupIds.includes(g.aadGroupId);
                return (
                  <label key={g.aadGroupId} className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={checked} className="accent-purple-600"
                      onChange={() => update({
                        viewerGroupIds: checked
                          ? draft.viewerGroupIds.filter(id => id !== g.aadGroupId)
                          : draft.viewerGroupIds.length < 20 ? [...draft.viewerGroupIds, g.aadGroupId] : draft.viewerGroupIds,
                      })} />
                    <span className="text-sm text-gray-800 truncate">{g.displayName}</span>
                  </label>
                );
              })}
              {visibleGroups.length === 0 && <div className="p-3 text-center text-xs text-gray-400">No groups found.</div>}
            </div>
            <p className="text-[11px] text-gray-400 mt-1">{draft.viewerGroupIds.length} of max 20 groups selected.</p>
          </section>

          {/* Live preview */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Preview</h3>
            <div className="border border-gray-200 rounded-lg p-2 overflow-x-auto bg-gray-50/50">
              {previewLayout
                ? <FlowDiagram layout={previewLayout} accent={accentColor} selectedId={null} onSelect={() => {}} />
                : previewFailed
                  ? <div className="py-8 text-center text-sm text-gray-500">Couldn't load the diagram engine. <button onClick={() => window.location.reload()} className="underline text-purple-600">Reload</button></div>
                  : <div className="py-8 text-center text-sm text-gray-400">Laying out diagram…</div>}
            </div>
          </section>

          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{error}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between sticky bottom-0 bg-white rounded-b-lg">
          <div>
            {canDelete && (confirmDelete ? (
              <span className="text-xs text-red-700">
                Delete this location?{' '}
                <button onClick={doDelete} disabled={busy} className="underline font-medium">Yes, delete</button>{' · '}
                <button onClick={() => setConfirmDelete(false)} className="underline">cancel</button>
              </span>
            ) : (
              <button onClick={() => setConfirmDelete(true)} className="text-xs text-gray-400 hover:text-red-600">Delete location</button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} disabled={busy}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={busy}
              className="px-4 py-1.5 text-sm text-white rounded font-medium disabled:opacity-60"
              style={{ backgroundColor: accentColor }}>
              {busy ? 'Saving…' : 'Save location'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Per-node form ----------

/** Collapsible sub-section inside a node card, so the richer guide detail
 *  doesn't bury the routing fields. */
function Fold({ title, hint, defaultOpen, children }: { title: string; hint?: string; defaultOpen?: boolean; children: ReactNode }) {
  return (
    <details className="group border-t border-gray-100 pt-2" open={defaultOpen}>
      <summary className="cursor-pointer list-none flex items-center gap-1.5 text-xs font-semibold text-gray-700">
        <ChevronRight className="w-3.5 h-3.5 text-gray-400 transition-transform group-open:rotate-90" />
        {title}
        {hint && <span className="font-normal text-gray-400">· {hint}</span>}
      </summary>
      <div className="mt-2 space-y-2.5">{children}</div>
    </details>
  );
}

function NodeForm({ node, nodeOptions, groups, queues, directory, voicemailGroups, audioPicker, onChange, onRemove }: {
  node: CallFlowNode;
  nodeOptions: Array<{ value: string; label: string }>;
  groups: Group[];
  queues: QueueOption[];
  directory: AgentOption[];
  voicemailGroups: Array<{ id: string; name?: string }>;
  audioPicker?: AudioPickerRenderer;
  onChange: (patch: any) => void;
  onRemove: () => void;
}) {
  const targets = nodeOptions.filter(o => o.value !== node.id);
  const targetSelect = (value: string | undefined, onSel: (v: string | undefined) => void, allowNone = true) => (
    <select value={value || ''} onChange={e => onSel(e.target.value || undefined)} className={inputCls}>
      {allowNone && <option value="">— none —</option>}
      {targets.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
  const hasPrompt = (p?: Prompt) => !!p && p.mode !== 'none';

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 uppercase tracking-wide font-medium shrink-0">
          {NODE_TYPE_LABEL[node.type]}
        </span>
        <input type="text" value={node.label} onChange={e => onChange({ label: e.target.value })}
          className="flex-1 px-2 py-1 text-sm font-medium border border-transparent hover:border-gray-200 focus:border-purple-500 rounded focus:outline-none" />
        {node.teamsId && <span className="text-[10px] text-gray-400 shrink-0" title={`Teams id ${node.teamsId}`}>from Teams</span>}
        <button onClick={onRemove} className="p-1 text-gray-300 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
      </div>

      {node.type === 'autoAttendant' && (
        <div className="space-y-2.5">
          <div>
            <label className={labelCls}>Greeting (text description)</label>
            <textarea value={node.greeting || ''} onChange={e => onChange({ greeting: e.target.value || undefined })}
              rows={2} className={inputCls} placeholder="Welcome to GoodLife Foods Bocholt…" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Language</label>
              <input type="text" value={node.language || ''} onChange={e => onChange({ language: e.target.value || undefined })}
                placeholder="de-DE" className={inputCls} />
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-700 mt-5">
              <input type="checkbox" checked={!!node.dialByName} onChange={e => onChange({ dialByName: e.target.checked })}
                className="accent-purple-600" />
              Dial by name enabled
            </label>
          </div>
          <div>
            <label className={labelCls}>Menu options</label>
            {node.menu.map((m, i) => (
              <div key={i} className="flex gap-2 mb-1.5 items-center">
                <input type="text" value={m.key} placeholder="1" className={`${inputCls} w-14`}
                  onChange={e => onChange({ menu: node.menu.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)) })} />
                <input type="text" value={m.label} placeholder="Sales" className={`${inputCls} flex-1`}
                  onChange={e => onChange({ menu: node.menu.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                <select value={m.targetId} className={`${inputCls} flex-1`}
                  onChange={e => onChange({ menu: node.menu.map((x, j) => (j === i ? { ...x, targetId: e.target.value } : x)) })}>
                  {targets.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button onClick={() => onChange({ menu: node.menu.filter((_, j) => j !== i) })}
                  className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {node.menu.length < 12 && targets.length > 0 && (
              <button onClick={() => onChange({ menu: [...node.menu, { key: String(node.menu.length + 1), label: '', targetId: targets[0].value }] })}
                className="text-xs text-purple-700 hover:underline flex items-center gap-1"><Plus className="w-3 h-3" />Add option</button>
            )}
          </div>
          <div>
            <label className={labelCls}>Operator (0) routes to</label>
            {targetSelect((node as AutoAttendantNode).operatorTargetId, v => onChange({ operatorTargetId: v }))}
          </div>

          <Fold title="What callers hear" hint="greeting and menu prompt in the guide" defaultOpen={hasPrompt(node.greetingPrompt) || hasPrompt(node.menuPrompt)}>
            <PromptEditor label="Greeting" value={node.greetingPrompt} onChange={v => onChange({ greetingPrompt: v })} audioPicker={audioPicker} />
            <PromptEditor label="Menu prompt" value={node.menuPrompt} onChange={v => onChange({ menuPrompt: v })} audioPicker={audioPicker} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Voice (text to speech)</label>
                <input type="text" value={node.voice || ''} onChange={e => onChange({ voice: e.target.value || undefined })} placeholder="Female" className={inputCls} />
              </div>
            </div>
          </Fold>

          <Fold title="Own hours (nested menu)" hint="leave empty when this menu follows the location's hours" defaultOpen={!!node.hours || !!node.afterHoursTargetId}>
            <HoursGrid value={node.hours} onChange={hours => onChange({ hours })} compact />
            <div>
              <label className={labelCls}>Outside these hours, route to</label>
              {targetSelect(node.afterHoursTargetId, v => onChange({ afterHoursTargetId: v }))}
            </div>
            <div>
              <label className={labelCls}>Holidays of this menu</label>
              <HolidaysEditor value={node.holidays || []} onChange={holidays => onChange({ holidays: holidays.length ? holidays : undefined })} />
            </div>
          </Fold>

          <Fold title="Guide display name" defaultOpen={!!node.displayName}>
            <LocalizedNameInput value={node.displayName} onChange={v => onChange({ displayName: v })} />
          </Fold>
        </div>
      )}

      {node.type === 'queue' && (
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className={labelCls}>Routing method</label>
              <select value={node.routing || ''} onChange={e => onChange({ routing: e.target.value || undefined })} className={inputCls}>
                <option value="">— not set —</option>
                {ROUTING_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Agents (security group)</label>
              <select value={node.agentsGroupId || ''} className={inputCls}
                onChange={e => {
                  const g = groups.find(x => x.aadGroupId === e.target.value);
                  onChange({ agentsGroupId: g?.aadGroupId, agentsGroupName: g?.displayName });
                }}>
                <option value="">— none —</option>
                {groups.filter(g => !g.orphaned).slice(0, 200).map(g =>
                  <option key={g.aadGroupId} value={g.aadGroupId}>{g.displayName}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Overflow after (calls)</label>
              <input type="number" min={0} max={200} value={node.overflowThreshold ?? ''} className={inputCls}
                onChange={e => onChange({ overflowThreshold: clampInt(e.target.value, 200) })} />
            </div>
            <div>
              <label className={labelCls}>Overflow routes to</label>
              {targetSelect(node.overflowTargetId, v => onChange({ overflowTargetId: v }))}
            </div>
            <div>
              <label className={labelCls}>Timeout (seconds)</label>
              <input type="number" min={0} max={2700} value={node.timeoutSeconds ?? ''} className={inputCls}
                onChange={e => onChange({ timeoutSeconds: clampInt(e.target.value, 2700) })} />
            </div>
            <div>
              <label className={labelCls}>Timeout routes to</label>
              {targetSelect(node.timeoutTargetId, v => onChange({ timeoutTargetId: v }))}
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Linked directory queue (makes the node dialable)</label>
              <select value={node.directoryQueueId || ''} className={inputCls}
                onChange={e => onChange({ directoryQueueId: e.target.value || undefined })}>
                <option value="">— none —</option>
                {queues.map(q => <option key={q.id} value={q.id}>{q.displayName}</option>)}
              </select>
            </div>
          </div>

          <Fold title="Agents" hint={`${node.agents?.length || 0} individual${node.agentGroupIds?.length ? ` + ${node.agentGroupIds.length} group(s)` : ''}`} defaultOpen={!!node.agents?.length}>
            <AgentsPicker value={node.agents || []} directory={directory} onChange={agents => onChange({ agents: agents.length ? agents : undefined })} />
          </Fold>

          <Fold title="Queue behaviour" hint="routing options, hold music, callback" defaultOpen={!!node.music || !!node.callback}>
            <QueueSettingsForm node={node as QueueFlowNode} onChange={patch => onChange(patch)} audioPicker={audioPicker} />
          </Fold>

          <Fold title="When nobody answers" hint="overflow, timeout, no agents" defaultOpen={!!node.overflow || !!node.timeout || !!node.noAgent}>
            <QueueExceptionForm kind="overflow" value={node.overflow} onChange={v => onChange({ overflow: v })} targets={targets} groups={voicemailGroups} audioPicker={audioPicker} />
            <QueueExceptionForm kind="timeout" value={node.timeout} onChange={v => onChange({ timeout: v })} targets={targets} groups={voicemailGroups} audioPicker={audioPicker} />
            <QueueExceptionForm kind="noAgent" value={node.noAgent} onChange={v => onChange({ noAgent: v })} targets={targets} groups={voicemailGroups} audioPicker={audioPicker} />
          </Fold>

          <Fold title="Guide display name" defaultOpen={!!node.displayName}>
            <LocalizedNameInput value={node.displayName} onChange={v => onChange({ displayName: v })} />
          </Fold>
        </div>
      )}

      {(node.type === 'voicemail' || node.type === 'person' || node.type === 'external') && (
        <div>
          <label className={labelCls}>
            {node.type === 'voicemail' ? 'Mailbox' : node.type === 'person' ? 'Person' : 'Phone number'}
          </label>
          <input type="text" value={node.detail || ''} onChange={e => onChange({ detail: e.target.value || undefined })}
            placeholder={node.type === 'voicemail' ? 'info@glfoods.com' : node.type === 'person' ? 'M. de Vries' : '+31 …'}
            className={inputCls} />
        </div>
      )}
    </div>
  );
}
